import { Button, Stack, Typography, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import LoadingState from './LoadingState'
import { ReactNode } from 'react'

export interface DropzoneProps {
  icon?: ReactNode
  label?: string
  buttonText?: string
  uploading?: boolean
  onDrop?: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent
  ) => void
}

export default function Dropzone({
  icon,
  label,
  buttonText,
  uploading = false,
  onDrop
}: DropzoneProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation()

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The dropzone state.
   */
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop
  })

  return (
    <Box
      minHeight={200}
      height="33vh"
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        borderStyle: 'dashed',
        borderWidth: 2,
        width: '100%',
        cursor: 'pointer'
      }}
      {...getRootProps()}
    >
      <Stack direction="column" justifyContent="center" alignItems="center" gap={4} width="100%">
        <input disabled={uploading} {...getInputProps()} />

        {uploading ? (
          <LoadingState />
        ) : (
          <>
            {icon}
            {label && <Typography variant="h6">{label}</Typography>}

            <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
              <Button component="label" variant="contained" color="primary">
                {buttonText ?? t('selectFileFromYourDevice')}
              </Button>
              <Typography>{t('orDragHere')}</Typography>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  )
}
